import { ColDef, ColGroupDef } from '@ag-grid-community/core';

import {
  AgColumnGroupHeaderComponent,
  AgColumnGroupHeaderExternalParams,
} from '@shared/ag-components/ag-column-group-header/ag-column-group-header';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { AuxExcelStyleKeys } from '@shared/utils/aux-excel-styles/aux-excel-style-keys';

import {
  qcGetFluxAnalysisColumns,
  qcGetFluxPrevValueColumn,
  qcGetInvoiceColumnDefaults,
  qcGetMoneyColumn,
  qcGetUnpaidInvoicesCellRenderer,
  QuarterCloseGridConfigContext,
} from './qc-grid-config-context';

export const qcQuarterSummaryColumnGroup = (
  ctx: QuarterCloseGridConfigContext,
  isFluxAnalysisAvailableAndOn: boolean
): ColGroupDef => {
  const openByDefault =
    localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_QUARTER_SUMMARY_GROUP) === 'true';

  const isCurrentQuarterHas3Months = ctx.selectedQuarterMonths.length === 3;

  return {
    children: getChildren(ctx, isFluxAnalysisAvailableAndOn),
    groupId: 'summaryGroup',
    headerClass: [
      'text-center',
      'font-semibold',
      'header-marker',
      'blue-marker',
      isCurrentQuarterHas3Months ? AuxExcelStyleKeys.ALTERNATE : '',
    ],
    headerGroupComponent: AgColumnGroupHeaderComponent,
    headerGroupComponentParams: <AgColumnGroupHeaderExternalParams>{
      onToggle: (isOpen: boolean) => {
        localStorage.setItem(LocalStorageKey.CLOSE_SUMMARY_QUARTER_SUMMARY_GROUP, String(isOpen));
      },
    },
    headerName: `${ctx.selectedQuarter.replace('-', ' ')} Summary`,
    openByDefault,
  };
};

// eslint-disable-next-line max-lines-per-function
const getChildren = (
  ctx: QuarterCloseGridConfigContext,
  isFluxAnalysisAvailableAndOn: boolean
): ColDef[] => {
  return [
    ...qcGetFluxPrevValueColumn(
      'previous_quarter_total_wp',
      'Previous Quarter Quarterly Expense',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    {
      ...qcGetMoneyColumn(),
      columnGroupShow: 'open',
      field: 'qend_quarterly_wp_estimate',
      headerClass: ['text-center', 'whitespace-normal'],
      headerName: `Quarterly Expense${ctx.isCurrentQuarterSelected ? ' Estimate' : ''}`,
    },
    ...qcGetFluxAnalysisColumns(
      'quarterly_expense_variance',
      'Quarterly Expense QoQ Var ($)',
      'quarterly_expense_variance_percentage',
      'Quarterly Expense QoQ Var (%)',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    ...qcGetFluxPrevValueColumn(
      'previous_quarter_total_invoiced',
      'Previous Quarter Quarterly Invoice',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    {
      ...qcGetMoneyColumn(),
      ...qcGetInvoiceColumnDefaults(ctx),
      columnGroupShow: 'open',
      field: 'qend_monthly_invoices_received',
      headerClass: ['text-center', 'whitespace-normal'],
      headerName: 'Quarterly Invoices Received',
    },
    ...qcGetFluxAnalysisColumns(
      'quarterly_invoices_variance',
      'Quarterly Invoices QoQ Var ($)',
      'quarterly_invoices_variance_percentage',
      'Quarterly Invoices QoQ Var (%)',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    {
      ...qcGetMoneyColumn(),
      columnGroupShow: 'open',
      field: 'qend_net_change_in_accruals',
      headerClass: ['text-center', 'whitespace-normal'],
      headerName: 'Net Change in Accruals / (Prepaid)',
      hide: isFluxAnalysisAvailableAndOn,
      maxWidth: 210,
    },
    {
      ...qcGetMoneyColumn(),
      columnGroupShow: 'open',
      field: 'qend_wp_ltd',
      headerName: 'Expense LTD',
    },
    {
      ...qcGetMoneyColumn(),
      ...qcGetInvoiceColumnDefaults(ctx),
      columnGroupShow: 'open',
      field: 'qend_invoiced_ltd',
      headerName: 'Invoiced LTD',
    },
    ...qcGetFluxPrevValueColumn(
      'previous_quarter_total_accrual',
      'Previous Quarter Accrual LTD',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    ...qcGetFluxPrevValueColumn(
      'previous_quarter_total_prepaid',
      'Previous Quarter Prepaid LTD',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    {
      ...qcGetMoneyColumn(),
      field: 'qend_accrual_ltd',
      headerName: 'Accrual LTD',
      minWidth: 125,
    },
    {
      ...qcGetMoneyColumn(),
      field: 'qend_prepaid_ltd',
      headerName: 'Prepaid LTD',
      minWidth: 125,
    },
    ...qcGetFluxAnalysisColumns(
      'quarterly_accrual_prepaid_difference',
      'Accrual/Prepaid LTD QoQ Var ($)',
      'quarterly_accrual_prepaid_difference_percentage',
      'Accrual/Prepaid LTD QoQ Var (%)',
      ctx.isCurrentQuarterSelected,
      !isFluxAnalysisAvailableAndOn
    ),
    ...getUnpaidInvoicesQuarterColumn(ctx, isFluxAnalysisAvailableAndOn),
  ];
};

const getUnpaidInvoicesQuarterColumn = (
  ctx: QuarterCloseGridConfigContext,
  hideByDefault: boolean
): ColDef[] => {
  const columns: ColDef[] = [];

  if (ctx.flags.isUnpaidColumnsVisible) {
    columns.push({
      ...qcGetMoneyColumn(),
      ...qcGetInvoiceColumnDefaults(ctx),
      columnGroupShow: 'open',
      field: 'qend_unpaid_invoices_ltd',
      headerName: 'Unpaid Invoices LTD',
      ...qcGetUnpaidInvoicesCellRenderer(),
      hide: hideByDefault,
    });
  }

  return columns;
};
